import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import { ESendByCallPetitions, ICallback, sendByCall } from '@r1-frontend/api-domru/api-request/v1/user/by-call';
import { backCall } from '@r1-frontend/api-domru/core-requests/api/v1/call/back';
import { useChat } from '@r1-frontend/entities/Chat';

import { Footer as FooterComponent, IFooterLinks } from '@r1-frontend/ui-react/organisms/Footer';
import { EThemes, ISendRequestHandleProps, ITheme } from '@r1-frontend/ui-react/organisms/Footer/components/CallRequestByTheme/types';
import { ACTIONS, CATEGORIES } from '@r1-frontend/shared/constants/analytics';

import EmailWidget from '~/src/components/Footer/emailWidget';
import { useFooterLinks } from '~/src/components/Footer/useFooterLinks';
import { dataLayerForCBPhonePush } from '~/src/helpers/eventsForDataLayer';
import { getMobileLink } from '~/src/helpers/mobile';
import { TState } from '~/src/store';
import { selectLegalEntity } from '~/src/store/selectors/city';
import { selectHasIntercom } from '~/src/store/selectors/menu/selectHasIntercom';

interface IFooter {
    withChat: boolean,
    withEmailWidget?: boolean,
    emailWidgetText?: string,
    text?: string,
}

interface ICobrandData {
    copyrightLink: string,
}

const COBRAND_DATA: Record<number, ICobrandData> = {
    556: {
        // todo: Добавить ссылку на ПД для Москва ЭрТХ
        copyrightLink: '',
    },
};

const FOOTER_LINKS: IFooterLinks = {
    copyrightLink: 'https://cdn-b2c.dom.ru/content/static/files/document/44561aae80e8223dd766d0f6a1da9294.pdf ',
};

export const Footer = ({  text, withEmailWidget, emailWidgetText = '', withChat }: IFooter) => {
    const {
        city: { currentCity: city, provider: { providerId } },
        auth: { isAuth, clientPersonal, accessToken, refreshToken },
    } = useSelector((state: TState) => state);
    const legalEntity = useSelector(selectLegalEntity);
    const hasIntercom = useSelector(selectHasIntercom);

    const { domain, internet, ctv, ktv, videocontrol } = city;
    const agreement = clientPersonal?.agreement;

    if (providerId in COBRAND_DATA) {
        FOOTER_LINKS.cobrandLegalEntity = legalEntity;
        FOOTER_LINKS.cobrandCopyrightLink = COBRAND_DATA[providerId]?.copyrightLink;
    }

    const sendRequestHandle = useCallback(async({ theme, fioValue, phoneValue }: ISendRequestHandleProps) => {
        let resp;
        const uid = isAuth ? agreement : null;

        if (theme.code === EThemes.BECOME_CLIENT) {
            resp = await doRequest.apiRequest<ICallback>(
                sendByCall(providerId, {
                    fio: fioValue,
                    phone: phoneValue,
                    petition: ESendByCallPetitions.RequestNew,
                }),
            );

            dataLayerForCBPhonePush({
                action: resp.isSuccess ? ACTIONS.finished : ACTIONS.error,
                requestId: null,
                option: resp.isSuccess ? `${theme.label}` : `${theme.label} | ${resp.error.message}`,
                isPopup: true,
                uid,
                category: CATEGORIES.applicationFormFooter,
            });
        } else {
            resp = await doRequest.coreRequests<unknown>(
                backCall({
                    phoneNumber: phoneValue,
                }),
            );

            dataLayerForCBPhonePush({
                action: resp.isSuccess ? ACTIONS.orderService : ACTIONS.errorService,
                requestId: null,
                option: resp.isSuccess ? `${theme.label}` : `${theme.label} | ${resp.error.message}`,
                isPopup: true,
                uid,
                category: CATEGORIES.applicationFormFooter,
            });
        }

        if (resp.isSuccess) {
            return { isError: false, message: 'Заявка успешно создана' };
        } else {
            return { isError: true, message: resp.error.message };
        }

    }, [providerId, agreement, domain, isAuth]);

    const onChangeHandle = useCallback((theme:ITheme)=> {
        dataLayerForCBPhonePush({
            action: theme.code === EThemes.BECOME_CLIENT ? ACTIONS.started : ACTIONS.startedService,
            requestId: null,
            option: `${theme.label}`,
            isPopup: true,
            uid: isAuth ? clientPersonal?.agreement : null,
            category: CATEGORIES.applicationFormFooter,
        });
    }, [clientPersonal, isAuth]);


    const servicesLinks = useFooterLinks({
        hasInternet: internet,
        hasCtv: ctv,
        hasKtv: ktv,
        hasVideocontrol: videocontrol,
        hasDomofon: hasIntercom,
        domain,
    });

    const onClickMobileButton = (mobileOs: string, e: React.SyntheticEvent<HTMLDivElement>) => {
        e.preventDefault();
        window.location.href = getMobileLink(accessToken, refreshToken, agreement, city, mobileOs);
    };

    const { open: openChatHandler } = useChat();

    const phone = city.supportPhone;

    return <>
        {withEmailWidget && <EmailWidget mainText={emailWidgetText}/>}
        <FooterComponent
            text={text}
            phone={phone}
            withChat={withChat}
            openChatHandler={() => openChatHandler()}
            onClickMobileButton={onClickMobileButton}
            servicesLinks={servicesLinks}
            sendRequestHandle={sendRequestHandle}
            onChangeHandle={onChangeHandle}
            links={FOOTER_LINKS}
        />
    </>;
};
